/**
 * The sound bank: every one-shot and loop the game plays, by name, with a
 * recipe from `sounds.ts`. A worker runs the recipes once at start
 * (`three/audio/bank.worker.ts`), so they cost the frame nothing; a few
 * variants of each keep repeats from sounding like repeats.
 *
 * Sounds with no high end are made at a lower rate: half the memory and
 * half the work, and no one hears the difference in a thunder roll.
 */

import type { Bird, Material, StepSurface } from './sounds.js';
import {
  BIRDS, MATERIALS, STEP_SURFACES, airBrake, babble, bell, birdSong, breath, canopyOpen, crickets, crossingBell, padChime, scooterBeep, trainClack, trainDoors, trainHorn, trainMotor, trainRoll, door, footstep, gull,
  impact, noiseLoop, rain, roofRain, scrape, shatter, thunder,
} from './sounds.js';

export interface BankEntry {
  id: string;
  /** Sample rate it is made at, Hz. */
  rate: number;
  /** Played round and round, not once. */
  loop?: boolean;
  make: (sampleRate: number) => Float32Array;
}

const HI = 44100;
const LO = 22050;
/** Variants of each crash, step and call. */
export const VARIANTS = 3;

/** A crash's two weights: a knock or a scrape, and a proper hit. */
export type HitSize = 'light' | 'heavy';

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

export const BANK: readonly BankEntry[] = [
  ...MATERIALS.flatMap(material => (['light', 'heavy'] as const).flatMap(size => range(VARIANTS).map(v => ({
    id: impactId(material, size, v), rate: HI,
    make: (sr: number) => impact(material, size === 'light' ? 0.25 : 0.85, 101 + v * 7 + MATERIALS.indexOf(material) * 31 + (size === 'heavy' ? 3 : 0), sr),
  })))),
  ...range(2).map(v => ({ id: `shatter:${v}`, rate: HI, make: (sr: number) => shatter(500 + v, sr) })),
  { id: 'scrape', rate: HI, loop: true, make: (sr: number) => scrape(77, sr) },
  ...STEP_SURFACES.flatMap(surface => [false, true].flatMap(heavy => range(VARIANTS).map(v => ({
    id: stepId(surface, heavy, v), rate: HI, make: (sr: number) => footstep(surface, heavy, 900 + v * 13 + (heavy ? 5 : 0), sr),
  })))),
  ...range(2).map(v => ({ id: `thunder:near:${v}`, rate: LO, make: (sr: number) => thunder(true, 40 + v, sr) })),
  ...range(2).map(v => ({ id: `thunder:far:${v}`, rate: LO, make: (sr: number) => thunder(false, 60 + v, sr) })),
  { id: 'rain:light', rate: HI, loop: true, make: (sr: number) => rain(false, 3, sr) },
  { id: 'rain:heavy', rate: HI, loop: true, make: (sr: number) => rain(true, 4, sr) },
  { id: 'rain:roof', rate: LO, loop: true, make: (sr: number) => roofRain(5, sr) },
  { id: 'bell', rate: 32000, make: (sr: number) => bell(9, sr) },
  ...BIRDS.flatMap(bird => range(VARIANTS).map(v => ({ id: birdId(bird, v), rate: HI, make: (sr: number) => birdSong(bird, 300 + v * 17 + BIRDS.indexOf(bird) * 3, sr) }))),
  ...range(VARIANTS).map(v => ({ id: `gull:${v}`, rate: HI, make: (sr: number) => gull(700 + v, sr) })),
  { id: 'crickets', rate: HI, loop: true, make: (sr: number) => crickets(8, sr) },
  { id: 'babble', rate: LO, loop: true, make: (sr: number) => babble(12, sr) },
  ...range(2).map(v => ({ id: `breath:out:${v}`, rate: LO, make: (sr: number) => breath(true, 0.7, 20 + v, sr) })),
  ...range(2).map(v => ({ id: `breath:in:${v}`, rate: LO, make: (sr: number) => breath(false, 0.7, 30 + v, sr) })),
  { id: 'train:roll', rate: LO, loop: true, make: (sr: number) => trainRoll(140, sr) },
  { id: 'train:motor:electric', rate: LO, loop: true, make: (sr: number) => trainMotor(true, 141, sr) },
  { id: 'train:motor:diesel', rate: LO, loop: true, make: (sr: number) => trainMotor(false, 142, sr) },
  ...range(2).map(v => ({ id: `train:clack:${v}`, rate: HI, make: (sr: number) => trainClack(150 + v, sr) })),
  { id: 'train:horn', rate: LO, make: (sr: number) => trainHorn(160, sr) },
  { id: 'train:doors', rate: HI, make: (sr: number) => trainDoors(161, sr) },
  { id: 'crossing:bell', rate: HI, make: (sr: number) => crossingBell(170, sr) },
  { id: 'ui:pad', rate: LO, make: (sr: number) => padChime(sr) },
  { id: 'airbrake', rate: HI, make: (sr: number) => airBrake(2, sr) },
  { id: 'door:shut', rate: HI, make: (sr: number) => door(true, 4, sr) },
  { id: 'door:open', rate: HI, make: (sr: number) => door(false, 6, sr) },
  { id: 'canopy', rate: LO, make: (sr: number) => canopyOpen(10, sr) },
  { id: 'scooter:on', rate: HI, make: (sr: number) => scooterBeep(true, sr) },
  { id: 'scooter:off', rate: HI, make: (sr: number) => scooterBeep(false, sr) },
  { id: 'noise:white', rate: HI, loop: true, make: (sr: number) => noiseLoop('white', 51, sr) },
  { id: 'noise:brown', rate: LO, loop: true, make: (sr: number) => noiseLoop('brown', 52, sr) },
];

export function impactId(material: Material, size: HitSize, variant: number): string {
  return `impact:${material}:${size}:${variant % VARIANTS}`;
}

export function stepId(surface: StepSurface, heavy: boolean, variant: number): string {
  return `step:${surface}:${heavy ? 'run' : 'walk'}:${variant % VARIANTS}`;
}

export function birdId(bird: Bird, variant: number): string {
  return `bird:${bird}:${variant % VARIANTS}`;
}

/**
 * The sound of a hit: which weight of crash, and how loud, from the change
 * in speed it caused (`dv`, m/s). A bump at walking pace is a soft knock; at
 * 50 km/h (14 m/s) into a wall it is as loud as anything in the game.
 */
export function hitSound(dv: number): { size: HitSize; gain: number } {
  const s = Math.max(0, Math.min(1, dv / 14));
  return { size: s < 0.35 ? 'light' : 'heavy', gain: Math.sqrt(s) };
}
